import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { skillColor } from "@/lib/skills";
import { Volume2, VolumeX } from "lucide-react";
import { t } from "@/i18n";

const LEVELS = ["Base", "A1", "A2", "B1", "B2", "C1", "C2"];

export default function Cheatsheets() {
  const [sheets, setSheets] = useState([]);
  const [level, setLevel] = useState(new URLSearchParams(window.location.search).get("level") || "Base");
  const [open, setOpen] = useState(null);
  const [speaking, setSpeaking] = useState(null);
  const [audioError, setAudioError] = useState("");
  const audioRef = useRef(null);
  const audioUrlRef = useRef(null);

  useEffect(() => { api.get("/content/cheatsheets").then((r) => setSheets(r.data)); }, []);

  // Ouverture automatique d'une fiche précise via ?open=<id>, utilisé par
  // exemple par l'exercice de racines pour renvoyer directement vers sa fiche.
  useEffect(() => {
    const wanted = new URLSearchParams(window.location.search).get("open");
    if (wanted && sheets.length) {
      const found = sheets.find((s) => s.id === wanted);
      if (found) {
        setOpen(found);
        if (found.level) setLevel(found.level);
      }
    }
  }, [sheets]);

  const list = sheets
    .filter((s) => s.level === level)
    .sort((a, b) => (a.id === "cs_full_alphabet" ? -1 : b.id === "cs_full_alphabet" ? 1 : 0));

  const speak = async (body, kind, index) => {
    const audioKey = `${kind}-${index}`;
    setAudioError("");
    setSpeaking(audioKey);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }

    try {
      const response = await api.get(`/content/alphabet/audio/${kind}/${encodeURIComponent(body.ar)}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      audioUrlRef.current = url;
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => setSpeaking(null);
      audio.onerror = () => {
        setSpeaking(null);
        setAudioError("Le son n’a pas pu être lu. Vérifie le volume de ton appareil.");
      };
      await audio.play();
    } catch {
      // Fallback explicite : certains navigateurs bloquent le MP3 ou n'ont
      // pas de voix arabe installée. On tente alors la synthèse vocale.
      if ("speechSynthesis" in window && typeof SpeechSynthesisUtterance !== "undefined") {
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(
          kind === "letter" ? (body.letter_audio_text || body.name || body.ar) : (body.word_audio_text || body.word_ar || body.ar)
        );
        utterance.lang = "ar-SA";
        utterance.rate = 0.7;
        utterance.onend = () => setSpeaking(null);
        utterance.onerror = () => {
          setSpeaking(null);
          setAudioError("Aucune voix arabe n’est disponible sur cet appareil.");
        };
        window.speechSynthesis.speak(utterance);
      } else {
        setSpeaking(null);
        setAudioError("La lecture audio n’est pas disponible sur ce navigateur.");
      }
    }
  };

  useEffect(() => () => {
    if (audioRef.current) audioRef.current.pause();
    if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
    if ("speechSynthesis" in window) window.speechSynthesis.cancel();
  }, []);

  const highlightLetter = (word, letter) => {
    if (!word || !letter || !word.includes(letter)) return word;
    const parts = word.split(letter);
    return parts.reduce((result, part, index) => (
      index === parts.length - 1
        ? [...result, part]
        : [...result, part, <strong key={`${letter}-${index}`} className="text-primary">{letter}</strong>]
    ), []);
  };

  const alphabetGroups = open?.id === "cs_full_alphabet"
    ? open.body.reduce((groups, body) => {
      const key = body.group_id || "alphabet";
      if (!groups[key]) groups[key] = { label: body.group_label || "Alphabet", items: [] };
      groups[key].items.push(body);
      return groups;
    }, {})
    : {};

  const closeSheet = () => {
    setOpen(null);
    setSpeaking(null);
    setAudioError("");
    if (audioRef.current) audioRef.current.pause();
    if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
    // Nettoie le paramètre ?open= pour ne pas rouvrir la fiche au prochain rendu.
    if (new URLSearchParams(window.location.search).get("open")) {
      window.history.replaceState({}, "", window.location.pathname);
    }
  };

  return (
    <div>
      <h1 className="font-head text-3xl font-bold mb-1">{t("navbar.fiches_memo")}</h1>
      <p className="text-muted-foreground mb-6">{t("cheatsheets.verifie_une_regle_en_10_secondes")}</p>

      <div className="flex gap-2 mb-6 overflow-x-auto">
        {LEVELS.map((l) => (
          <button key={l} data-testid={`cs-level-${l}`} onClick={() => setLevel(l)}
            className={`px-4 py-2 rounded-xl text-sm font-medium border transition ${level === l ? "bg-primary text-primary-foreground border-primary" : "hover:bg-secondary"}`}>{l}</button>
        ))}
      </div>

      {list.length === 0 ? (
        <div className="rounded-2xl border bg-card p-8 text-center text-muted-foreground">{t("cheatsheets.fiches")} {level} {t("cheatsheets.bientot_disponibles")}</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((s) => (
            <button key={s.id} data-testid={`cs-${s.id}`} disabled={!s.available}
              onClick={() => setOpen(s)}
              className={`rounded-2xl border bg-card p-5 text-left transition ${
                s.id === "cs_full_alphabet"
                  ? "border-2 border-primary bg-primary/5 shadow-md"
                  : ""
              } ${s.available ? "card-hover" : "opacity-50"}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: skillColor(s.skill) }} />
                <span className="font-semibold">{s.category}</span>
                {s.id === "cs_full_alphabet" && (
                  <span className="ml-auto rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold text-primary-foreground">
                    {t("cheatsheets.recommandee")}
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground">{s.summary}</p>
              {!s.available && <span className="text-xs text-ocre mt-2 inline-block">{t("cheatsheets.bientot_disponible")}</span>}
            </button>
          ))}
        </div>
      )}

      {open && open.id === "cs_full_alphabet" && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-background" data-testid="alphabet-sheet">
          <div className="alphabet-print-sheet mx-auto min-h-screen max-w-7xl px-4 py-5 sm:px-8 lg:px-12">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b pb-4">
              <div>
                <div className="text-sm font-semibold uppercase tracking-[0.18em] text-primary">{t("cheatsheets.harf_fiche_memo")}</div>
                <h2 className="font-head text-3xl font-bold sm:text-4xl">{t("cheatsheets.l_alphabet_arabe")}</h2>
                <p className="mt-1 text-sm text-muted-foreground">{open.summary}</p>
              </div>
              <div className="flex gap-2 print:hidden">
                <button type="button" onClick={() => window.print()} className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-secondary">
                  {t("cheatsheets.imprimer_la_fiche")}
                </button>
                <button type="button" onClick={closeSheet} className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90">
                  {t("tutorial.fermer")}
                </button>
              </div>
            </div>

            <div className="my-5 grid gap-4 lg:grid-cols-[1.1fr_1fr]">
              <div className="rounded-2xl border-2 border-primary/30 bg-primary/5 p-5">
                <h3 className="font-head text-xl font-bold">{t("cheatsheets.comment_lire_chaque_ligne")}</h3>
                <div className="mt-3 grid gap-2 text-sm">
                  <p><strong>{t("cheatsheets.lettre")}</strong> {t("cheatsheets.le_caractere_arabe_que_tu_dois")}</p>
                  <p><strong>{t("account.nom")}</strong> {t("cheatsheets.le_nom_utilise_dans_les_reponses")} <strong>tha</strong>.</p>
                  <p><strong>{t("cheatsheets.son")}</strong> {t("cheatsheets.le_bruit_produit_dans_un_mot")} <strong>th</strong>.</p>
                  <p><strong>{t("cheatsheets.mot_exemple")}</strong> {t("cheatsheets.un_vrai_mot_pour_entendre_le")}</p>
                </div>
                <div className="mt-4 rounded-xl bg-background/80 p-3 text-sm">
                  <span className="arabic text-3xl" dir="rtl">ث</span>
                  <span className="mx-2 font-bold">→</span>
                  <strong>{t("cheatsheets.nom_tha")}</strong>
                  <span className="mx-2 text-muted-foreground">·</span>
                  <strong>{t("cheatsheets.son_th")}</strong>
                  <span className="mx-2 text-muted-foreground">·</span>
                  <span className="arabic text-2xl" dir="rtl">ثَوْب</span>
                </div>
              </div>
              <div className="rounded-2xl border bg-card p-5">
                <h3 className="font-head text-xl font-bold">{t("cheatsheets.pour_reussir_les_exercices")}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{open.exercise_tip}</p>
                <h4 className="mt-4 text-sm font-bold">{t("cheatsheets.les_differences_importantes")}</h4>
                <ul className="mt-2 grid gap-1.5 text-xs text-muted-foreground sm:grid-cols-2">
                  {(open.family_notes || []).map((note) => <li key={note}>• {note}</li>)}
                </ul>
              </div>
            </div>

            <section className="mb-7 rounded-2xl border-2 border-primary/20 bg-card p-5 sm:p-6">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.16em] text-primary">{t("cheatsheets.la_methode_complete")}</div>
                  <h3 className="mt-1 font-head text-2xl font-bold">{t("cheatsheets.ce_qu_il_faut_vraiment_apprendre")}</h3>
                </div>
                <div className="rounded-lg bg-primary/10 px-3 py-2 text-xs font-bold text-primary">
                  {open.reading_formula}
                </div>
              </div>
              <div className="mt-5 grid gap-3 md:grid-cols-5">
                {(open.learning_path || []).map((step, index) => (
                  <div key={step.step} className="relative rounded-xl border bg-background p-4">
                    <div className="flex items-center gap-2">
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">{step.step}</span>
                      <span className="text-xs font-bold text-primary">{step.short}</span>
                    </div>
                    <h4 className="mt-3 font-semibold">{step.title}</h4>
                    <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{step.description}</p>
                    {index < (open.learning_path || []).length - 1 && (
                      <span className="absolute -right-3 top-6 z-10 hidden text-lg font-bold text-primary/50 md:block">→</span>
                    )}
                  </div>
                ))}
              </div>
            </section>

            {audioError && (
              <div className="mb-4 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
                {audioError}
              </div>
            )}

            <div className="space-y-7">
              {Object.values(alphabetGroups).map((group) => (
                <section key={group.label} className="break-inside-avoid">
                  <h3 className="mb-3 border-b-2 border-primary/20 pb-2 font-head text-xl font-bold">{group.label}</h3>
                  <div className="grid gap-3 md:grid-cols-2">
                    {group.items.map((b) => {
                      const index = open.body.indexOf(b);
                      return (
                        <article key={b.ar} className="grid grid-cols-[5rem_1fr] gap-4 rounded-2xl border bg-card p-4 shadow-sm">
                          <div className="flex flex-col items-center justify-center rounded-xl bg-primary/5">
                            <div className="arabic text-5xl leading-none" dir="rtl">{b.ar}</div>
                            <div className="mt-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("cheatsheets.lettre_2")}</div>
                            <button
                              type="button"
                              data-testid={`letter-audio-${index}`}
                              onClick={() => speak(b, "letter", index)}
                              className={`mt-2 inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-semibold transition print:hidden ${
                                speaking === `letter-${index}` ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary hover:bg-primary/20"
                              }`}
                              aria-label={`Écouter la lettre ${b.name}`}
                            >
                              {speaking === `letter-${index}` ? <VolumeX size={12} /> : <Volume2 size={12} />}
                              {speaking === `letter-${index}` ? t("cheatsheets.lecture") : t("cheatsheets.la_lettre")}
                            </button>
                          </div>
                          <div className="min-w-0">
                            <div className="grid gap-1 text-sm sm:grid-cols-2">
                              <div><span className="font-semibold">{t("account.nom")}</span> {b.name}</div>
                              <div><span className="font-semibold">{t("cheatsheets.son")}</span> {b.translit}</div>
                            </div>
                            <div className="mt-3 rounded-xl bg-secondary/60 p-3">
                              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("cheatsheets.mot_exemple_2")}</div>
                              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                                <span className="arabic text-3xl" dir="rtl">{highlightLetter(b.word_ar, b.ar)}</span>
                                <span className="font-semibold">{b.word_translit}</span>
                                <span className="text-sm text-muted-foreground">({b.word_fr})</span>
                              </div>
                              <button
                                type="button"
                                data-testid={`word-audio-${index}`}
                                onClick={() => speak(b, "word", index)}
                                className={`mt-3 inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition print:hidden ${
                                  speaking === `word-${index}`
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-primary/10 text-primary hover:bg-primary/20"
                                }`}
                                aria-label={`Écouter le mot exemple de ${b.name}`}
                              >
                                {speaking === `word-${index}` ? <VolumeX size={14} /> : <Volume2 size={14} />}
                                {speaking === `word-${index}` ? t("cheatsheets.lecture") : t("cheatsheets.le_mot")}
                              </button>
                            </div>
                            <p className="mt-2 text-xs text-primary"><strong>{t("cheatsheets.a_retenir")}</strong> {b.hint}</p>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          </div>
        </div>
      )}

      {open && open.id === "cs_root_full" && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-background" data-testid="roots-sheet">
          <div className="alphabet-print-sheet mx-auto min-h-screen max-w-4xl px-4 py-5 sm:px-8">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b pb-4">
              <div>
                <div className="text-sm font-semibold uppercase tracking-[0.18em] text-primary">{t("cheatsheets.harf_fiche_memo")}</div>
                <h2 className="font-head text-3xl font-bold sm:text-4xl">{t("cheatsheets.le_systeme_des_racines")}</h2>
                <p className="mt-1 text-sm text-muted-foreground">{open.summary}</p>
              </div>
              <div className="flex gap-2 print:hidden">
                <button type="button" onClick={() => window.print()} className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-secondary">
                  {t("cheatsheets.imprimer_la_fiche")}
                </button>
                <button type="button" onClick={closeSheet} className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90">
                  {t("tutorial.fermer")}
                </button>
              </div>
            </div>

            <div className="space-y-6 py-6">
              {(open.sections || []).map((sec, si) => (
                <section key={si} className="break-inside-avoid rounded-2xl border bg-card p-5 sm:p-6">
                  <h3 className="font-head text-xl font-bold mb-3">{sec.title}</h3>
                  {sec.intro && <p className="text-sm leading-relaxed mb-3">{sec.intro}</p>}

                  {sec.key && (
                    <div className="rounded-xl border-l-4 border-primary bg-primary/5 px-3 py-2 text-sm font-medium mb-3">
                      {sec.key}
                    </div>
                  )}

                  {(sec.examples || []).length > 0 && (
                    <div className="grid gap-2 sm:grid-cols-2 mb-3">
                      {sec.examples.map((ex, ei) => (
                        <div key={ei} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 rounded-xl bg-secondary/50 px-3 py-2">
                          <span className="arabic text-lg font-bold" dir="rtl">{ex.ar}</span>
                          {ex.translit && <span className="italic text-muted-foreground text-xs">{ex.translit}</span>}
                          <span className="text-xs">— {ex.fr}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {sec.warning && (
                    <div className="rounded-xl border-l-4 border-destructive bg-destructive/10 px-3 py-2 text-sm mb-2">
                      <span className="font-semibold text-destructive">{t("cheatsheets.piege")} </span>{sec.warning}
                    </div>
                  )}
                  {sec.tip && (
                    <div className="rounded-xl border-l-4 border-amber-500 bg-amber-500/10 px-3 py-2 text-sm">
                      <span className="font-semibold text-amber-700 dark:text-amber-400">{t("cheatsheets.astuce")} </span>{sec.tip}
                    </div>
                  )}
                </section>
              ))}
            </div>
          </div>
        </div>
      )}

      {open && open.id !== "cs_full_alphabet" && open.id !== "cs_root_full" && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={closeSheet}>
          <div className="bg-card rounded-2xl border max-w-lg w-full p-6 max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="cs-modal">
            <h2 className="font-head text-xl font-bold mb-1">{open.category}</h2>
            <p className="text-muted-foreground text-sm mb-4">{open.summary}</p>
            <div className="space-y-2">
              {open.body.map((b, i) => (
                <div key={i} className="rounded-xl border p-3">
                  {b.ar && <div className="arabic text-2xl" dir="rtl">{b.ar}</div>}
                  {b.translit && <div className="text-xs text-muted-foreground">{b.translit}</div>}
                  <div className="text-sm">{b.fr}</div>
                  {b.hint && <div className="text-xs text-primary mt-1">{t("cheatsheets.memo")} {b.hint}</div>}
                </div>
              ))}
            </div>
            <button onClick={closeSheet} className="mt-5 w-full py-2.5 rounded-xl border font-medium hover:bg-secondary transition">{t("tutorial.fermer")}</button>
          </div>
        </div>
      )}
    </div>
  );
}
